import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useRegisterMutation } from '../../app/services/users';
import { fetchUsers } from '../../features/users/usersSlice';
import { getPermissions } from '../../constants/options';
import { commonTextFieldStyles } from '../../utils/textFieldStyles';
import { useTranslation } from 'react-i18next';
import { validateFormData } from '../../helpers';
import { postUserSchema } from '../../schemas/postUserSchema';
import { useCommonFormValidation } from '../../hooks/useFormValidation';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import ConfirmDiscardDialog from '../ConfirmDiscardDialog';

import { MenuItem, TextField } from '@mui/material';
import {
  ModalButton,
  ModalAddButton,
  ModalWrapper,
  ModalFormContainer,
  ModalErrorDisplay,
} from '../ModalComponents';

const initialForm = {
  firstname: '',
  lastname: '',
  email: '',
  role: 'admin',
};

export default function ModalAddUser() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errorResponse, setErrorResponse] = useState('');
  const [wasSuccessful, setWasSuccessful] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [registerUser, { isLoading, error }] = useRegisterMutation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const permissions = getPermissions(t);

  const {
    errors: { firstnameError, lastnameError, emailError, roleError },
    setErrors,
    clearAllErrors,
  } = useCommonFormValidation();

  const { guardClose, confirmProps } = useUnsavedChangesWarning(isDirty, open);

  const handleOpen = () => setOpen(true);
  const doClose = () => {
    setOpen(false);
    setForm(initialForm);
    clearAllErrors();
    setWasSuccessful(false);
    setIsDirty(false);
  };
  const handleClose = () => guardClose(doClose);

  useEffect(() => {
    if (error) {
      console.log(error);
      setErrorResponse(error.data.message);
    }
  }, [error]);

  useEffect(() => {
    if (wasSuccessful && !isLoading && !error) {
      doClose();
      setErrorResponse('');
      dispatch(fetchUsers());
      setWasSuccessful(false);
    }
  }, [wasSuccessful, isLoading, error, dispatch]);

  const handleChange = (e) => {
    setErrorResponse('');
    setIsDirty(true);
    if (e.target.name === 'permission') {
      setForm({
        ...form,
        permission: e.target.value,
      });
    } else {
      setForm({
        ...form,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateFormData(t, postUserSchema, form);
    if (!!validationErrors) {
      setErrors(validationErrors);
      return;
    }

    try {
      await registerUser(form);
      setWasSuccessful(true);
    } catch (err) {
      console.error('Error registering user:', err);
    }
  };

  const actions = (
    <>
      <ModalButton
        variant="outlined"
        color="primary"
        onClick={handleClose}
      >
        {t('modals.cancel')}
      </ModalButton>
      <ModalButton
        variant="contained"
        color="primary"
        onClick={handleSubmit}
      >
        {t('modals.create')}
      </ModalButton>
    </>
  );

  return (
    <div>
      <ModalAddButton onClick={handleOpen} />
      <ModalWrapper
        open={open}
        handleClose={handleClose}
        title={t('generic.createNewUser')}
        actions={actions}
      >
        <ModalFormContainer>
          <TextField
            label={t('generic.firstName')}
            variant="outlined"
            name="firstname"
            onChange={handleChange}
            fullWidth
            error={!!firstnameError}
            helperText={firstnameError ?? ' '}
            sx={commonTextFieldStyles}
          />
          <TextField
            label={t('generic.lastName')}
            variant="outlined"
            name="lastname"
            onChange={handleChange}
            fullWidth
            error={!!lastnameError}
            helperText={lastnameError ?? ' '}
            sx={commonTextFieldStyles}
          />
          <TextField
            label={t('generic.email')}
            variant="outlined"
            name="email"
            onChange={handleChange}
            fullWidth
            error={!!emailError}
            helperText={emailError ?? ' '}
            sx={commonTextFieldStyles}
          />
          <TextField
            defaultValue="admin"
            select
            label={t('generic.permission')}
            name="role"
            onChange={handleChange}
            error={!!roleError}
            helperText={roleError ?? ' '}
            sx={commonTextFieldStyles}
          >
            {permissions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </ModalFormContainer>
        <ModalErrorDisplay errorResponse={errorResponse} />
      </ModalWrapper>
      <ConfirmDiscardDialog {...confirmProps} />
    </div>
  );
}
